import { useState, useEffect, createElement } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Eye, Ear, Droplets, Wind, Flower2, TreePine } from 'lucide-react';

interface SensoryPractice {
  id: string;
  practice_date: string;
  practice_type: string;
  sense_focused: string;
  duration_minutes: number;
  observations: string;
  intuitive_insights: string;
  rating: number;
}

const practiceTypes = [
  {
    value: 'sight',
    label: 'Visual Awareness',
    icon: Eye,
    exercises: [
      'Notice 5 things you\'ve never seen before in a familiar place',
      'Study the play of light and shadow for 10 minutes',
      'Observe colors deeply - find 10 shades of one color in nature',
      'Practice peripheral vision - soften your gaze and notice everything'
    ],
    intuitionLink: 'Clairvoyance begins with truly seeing the physical world. Training your eyes to notice subtle details awakens your inner vision.'
  },
  {
    value: 'sound',
    label: 'Auditory Awareness', 
    icon: Ear,
    exercises: [
      'Sit still and count how many distinct sounds you can hear',
      'Listen to the space between sounds',
      'Focus on the furthest sound you can detect',
      'Notice the quality and texture of sounds, not just what they are'
    ],
    intuitionLink: 'Clairaudience develops when you learn to hear beyond surface noise. Practice listening to the subtle frequencies around you.'
  },
  {
    value: 'touch',
    label: 'Tactile Awareness',
    icon: Droplets,
    exercises: [
      'Touch 5 different textures mindfully, eyes closed',
      'Feel the temperature variations in a room',
      'Notice the sensation of air on your skin',
      'Hold an object and sense its energy through your palms'
    ],
    intuitionLink: 'Clairsentience is rooted in physical sensation. Your body is constantly receiving intuitive information through touch and feeling.'
  },
  {
    value: 'smell',
    label: 'Olfactory Awareness',
    icon: Wind,
    exercises: [
      'Identify 3 scents in your current environment',
      'Practice scent meditation with essential oils',
      'Notice how different scents affect your mood and energy',
      'Walk in nature and identify plant scents'
    ],
    intuitionLink: 'Clairalience (psychic smell) often appears spontaneously. Regular scent awareness training opens this gift.'
  },
  {
    value: 'taste',
    label: 'Gustatory Awareness',
    icon: Flower2,
    exercises: [
      'Eat one thing mindfully, noticing every flavour note',
      'Practice distinguishing subtle taste differences',
      'Notice how foods affect your energy field',
      'Explore the emotional memories connected to tastes'
    ],
    intuitionLink: 'Clairgustance (psychic taste) connects deeply to memory and emotion. Conscious tasting awakens this rare gift.'
  },
  {
    value: 'nature',
    label: 'Nature Connection',
    icon: TreePine,
    exercises: [
      'Sit with a tree and tune into its energy',
      'Walk barefoot on earth for 20 minutes (grounding)',
      'Watch clouds and notice intuitive messages in shapes',
      'Spend time near water and notice how it affects your intuition',
      'Practice "forest bathing" - immerse all senses in nature'
    ],
    intuitionLink: 'Nature is the ultimate intuition teacher. Natural environments clear mental clutter and amplify psychic receptivity. The earth\'s electromagnetic field supports your energy body, animals reflect spiritual messages, and plants hold ancient wisdom. Regular nature immersion isn\'t just nice - it\'s essential for strong intuitive development.'
  }
];

export default function IntuitionDevelopment() {
  const [practices, setPractices] = useState<SensoryPractice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [formData, setFormData] = useState({
    practice_type: '',
    sense_focused: '',
    duration_minutes: 15,
    observations: '',
    intuitive_insights: '',
    rating: 5
  });

  useEffect(() => {
    loadPractices();
  }, []);

  const loadPractices = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('sensory_practices')
        .select('*')
        .eq('user_id', user.id)
        .order('practice_date', { ascending: false });

      if (error) throw error;
      setPractices(data || []);
    } catch (error) {
      console.error('Error loading practices:', error);
      toast.error('Failed to load practices');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in');
        return;
      }

      const { error } = await supabase
        .from('sensory_practices')
        .insert({
          user_id: user.id,
          ...formData
        });

      if (error) throw error;

      toast.success('Practice logged!');
      setShowForm(false);
      setSelectedType('');
      setFormData({
        practice_type: '',
        sense_focused: '',
        duration_minutes: 15,
        observations: '',
        intuitive_insights: '',
        rating: 5
      });
      loadPractices();
    } catch (error) {
      console.error('Error saving practice:', error);
      toast.error('Failed to save practice');
    }
  };

  const getSenseStats = () => {
    const stats: Record<string, number> = {};
    practices.forEach(p => {
      stats[p.sense_focused] = (stats[p.sense_focused] || 0) + 1;
    });
    return stats;
  };

  const senseStats = getSenseStats();
  const selectedPractice = practiceTypes.find(p => p.value === selectedType);

  if (loading) {
    return <div className="text-muted-foreground">Loading practices...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-br from-secondary/10 to-accent/10 border-accent/20">
        <h2 className="text-2xl font-bold mb-2">Intuition Development</h2>
        <p className="text-muted-foreground mb-4">
          Strengthen your intuition by training your physical senses and connecting with nature. Every psychic ability begins with heightened awareness of the physical world.
        </p>

        <div className="bg-background/50 rounded-lg p-4 mb-4">
          <h3 className="font-semibold mb-2">Why Physical Senses Matter</h3>
          <p className="text-sm text-muted-foreground">
            Your intuition speaks through your body first. Training your five physical senses sharpens your ability to perceive subtle energies, psychic impressions, and spiritual messages. Nature amplifies this connection exponentially.
          </p>
        </div>

        <Button onClick={() => setShowForm(!showForm)} className="w-full bg-gradient-to-r from-secondary to-accent hover:from-secondary/90 hover:to-accent/90">
          {showForm ? 'Cancel' : 'Log Sensory Practice'}
        </Button>
      </Card>

      {showForm && (
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Select Practice Type</Label>
              <Select 
                value={selectedType} 
                onValueChange={(value) => {
                  setSelectedType(value);
                  setFormData({ ...formData, sense_focused: value });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a sense to develop" />
                </SelectTrigger>
                <SelectContent>
                  {practiceTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        {createElement(type.icon, { className: 'h-4 w-4' })}
                        <span>{type.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedPractice && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-3 animate-fade-in">
                <div className="flex items-center gap-2 mb-2">
                  {createElement(selectedPractice.icon, { 
                    className: 'h-5 w-5 text-primary' 
                  })}
                  <h4 className="font-semibold">{selectedPractice.label}</h4>
                </div>
                
                <div>
                  <p className="text-sm font-semibold mb-1">Try these exercises:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {selectedPractice.exercises.map((exercise, idx) => (
                      <li key={idx}>• {exercise}</li>
                    ))}
                  </ul>
                </div>

                <div className="bg-primary/10 rounded p-3">
                  <p className="text-sm">
                    <span className="font-semibold">🔮 Intuition Link:</span> {selectedPractice.intuitionLink}
                  </p>
                </div>
              </div>
            )}

            {selectedType && (
              <>
                <div>
                  <Label>What did you practice?</Label>
                  <Input
                    value={formData.practice_type}
                    onChange={(e) => setFormData({ ...formData, practice_type: e.target.value })}
                    placeholder="E.g., 'Listened to forest sounds' or 'Touched tree bark mindfully'"
                    required
                  />
                </div>

                <div>
                  <Label>Duration (minutes)</Label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                    min="1"
                    max="120"
                  />
                </div>

                <div>
                  <Label>What did you observe?</Label>
                  <Textarea
                    value={formData.observations}
                    onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                    placeholder="Describe what you noticed with your senses..."
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <Label>Intuitive Insights (optional)</Label>
                  <Textarea
                    value={formData.intuitive_insights}
                    onChange={(e) => setFormData({ ...formData, intuitive_insights: e.target.value })}
                    placeholder="Did any intuitive impressions, feelings, or knowings arise?"
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Experience Rating: {formData.rating}/10</Label>
                  <Slider
                    value={[formData.rating]}
                    onValueChange={(value) => setFormData({ ...formData, rating: value[0] })}
                    max={10}
                    min={1}
                    step={1}
                    className="mt-2"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    How connected/aware did you feel?
                  </p>
                </div>

                <Button type="submit" className="w-full bg-gradient-to-r from-secondary to-accent hover:from-secondary/90 hover:to-accent/90">Save Practice</Button>
              </>
            )}
          </form>
        </Card>
      )}

      {/* Progress Overview */}
      {Object.keys(senseStats).length > 0 && (
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Your Development Progress</h3>
          <div className="space-y-3">
            {practiceTypes.map(type => {
              const count = senseStats[type.value] || 0;
              const Icon = type.icon;
              return (
                <div key={type.value} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-primary" />
                    <span className="font-medium">{type.label}</span>
                  </div>
                  <Badge variant="secondary">{count} practice{count !== 1 ? 's' : ''}</Badge>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Recent Practices */}
      <div className="space-y-3">
        <h3 className="font-semibold">Recent Practices</h3>
        {practices.length === 0 ? (
          <Card className="p-6 text-center text-muted-foreground">
            <p>No practices logged yet. Start training your senses to awaken your intuition!</p>
          </Card>
        ) : (
          practices.slice(0, 10).map(practice => {
            const typeInfo = practiceTypes.find(t => t.value === practice.sense_focused);
            const Icon = typeInfo?.icon || Eye;
            
            return (
              <Card key={practice.id} className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">{practice.practice_type}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(practice.practice_date).toLocaleDateString()} • {practice.duration_minutes} min • {practice.rating}/10
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline">{typeInfo?.label}</Badge>
                </div>
                
                <p className="text-sm text-muted-foreground mt-2">{practice.observations}</p>
                
                {practice.intuitive_insights && (
                  <div className="mt-2 p-2 bg-primary/10 rounded">
                    <p className="text-sm">
                      <span className="font-semibold">🔮 Intuitive Insight:</span> {practice.intuitive_insights}
                    </p>
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}